import React, { useState, useContext, useEffect } from 'react';
import { ThemeContext } from '../ThemeContext';
import styled from 'styled-components';

const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: var(--color-card);
  box-shadow: 
    0 1px 3px var(--color-shadow),
    0 0 20px var(--color-quantum-glow);
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 100;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-quantum-glow);

  @media (max-width: 768px) {
    padding: 0.7rem 1rem;
  }
  
  @media (max-width: 576px) {
    padding: 0.5rem 0.8rem;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text);
  background: linear-gradient(45deg, var(--color-text), var(--color-quantum-accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.1rem;
  }
`;

const NavContent = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 0;
    margin-left: auto; /* Move to right side on mobile */
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 60px;
    right: 1rem; /* Position from right edge */
    width: 200px;
    background-color: var(--color-card);
    padding: 1.2rem;
    box-shadow: 0 5px 15px var(--color-shadow);
    gap: 1.2rem;
    z-index: 100;
    border-radius: 10px;
  }
  
  @media (max-width: 576px) {
    top: 50px;
    right: 0.8rem; /* Adjust for smaller screens */
    padding: 1rem;
    gap: 1rem;
    width: 180px;
  }
`;

const NavLink = styled.a`
  color: var(--color-text);
  text-decoration: none;
  font-weight: ${props => props.active ? '600' : '400'};
  position: relative;
  font-size: 1rem;
  
  &:hover {
    color: var(--color-primary);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: ${props => props.active ? '100%' : '0'};
    height: 2px;
    bottom: -5px;
    left: 0;
    background-color: var(--color-primary);
    transition: width 0.3s;
  }
  
  &:hover::after {
    width: 100%;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 576px) {
    font-size: 0.85rem;
  }
`;

const HamburgerIcon = styled.div`
  display: none;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
    order: 1; /* Put hamburger after toggle button */
  }

  div {
    width: 25px;
    height: 3px;
    background-color: var(--color-text);
    margin: 5px 0;
    transition: all 0.3s;

    &:nth-child(1) {
      transform: ${({ isOpen }) => isOpen ? 'rotate(-45deg) translate(-5px, 6px)' : 'none'};
    }

    &:nth-child(2) {
      opacity: ${({ isOpen }) => isOpen ? '0' : '1'};
    }

    &:nth-child(3) {
      transform: ${({ isOpen }) => isOpen ? 'rotate(45deg) translate(-5px, -6px)' : 'none'};
    }
  }
  
  @media (max-width: 576px) {
    div {
      width: 22px;
      height: 2px;
      margin: 4px 0;
    }
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  color: var(--color-primary);
  transition: color 0.3s;

  &:hover {
    color: var(--color-secondary);
  }

  @media (max-width: 768px) {
    margin-right: 0; /* Add some space before hamburger */
    order: 0; /* Put toggle button before hamburger */
  }
`;

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const navHeight = 80; // Height of the navbar
    const offsetPosition = element.offsetTop - navHeight;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [activeSection, setActiveSection] = useState('home');

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleSectionClick = (section) => {
    setActiveSection(section);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'experience', 'patents', 'awards', 'contact'];
      let currentSection = 'home';

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            currentSection = section;
          }
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleThemeToggle = () => {
    toggleTheme();
  };

  return (
    <NavContainer>
      <Logo>SURENDAR VP</Logo>
      <NavContent>
        <NavLinks isOpen={isOpen}>
          <NavLink 
            href="#home" 
            active={activeSection === 'home'} 
            onClick={() => handleSectionClick('home')}
          >
            Home
          </NavLink>
          <NavLink 
            href="#about" 
            active={activeSection === 'about'}
            onClick={() => handleSectionClick('about')}
          >
            About
          </NavLink>
          <NavLink 
            href="#experience" 
            active={activeSection === 'experience'}
            onClick={() => handleSectionClick('experience')}
          >
            Experience
          </NavLink>
          <NavLink 
            href="#patents" 
            active={activeSection === 'patents'}
            onClick={() => handleSectionClick('patents')}
          >
            Patents
          </NavLink>
          <NavLink 
            href="#awards" 
            active={activeSection === 'awards'}
            onClick={() => handleSectionClick('awards')}
          >
            Awards
          </NavLink>
          <NavLink 
            href="#contact" 
            active={activeSection === 'contact'}
            onClick={() => handleSectionClick('contact')}
          >
            Contact
          </NavLink>
        </NavLinks>
        <ToggleButton 
          onClick={handleThemeToggle} 
          aria-label="Toggle dark mode"
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </ToggleButton>
        <HamburgerIcon 
          isOpen={isOpen} 
          onClick={toggleMenu}
        >
          <div></div>
          <div></div>
          <div></div>
        </HamburgerIcon>
      </NavContent>
    </NavContainer>
  );
};

export default Navbar;